// 時間前注文 一覧 RPC response (= IntradayOrderRow) を 入札作成タブの表示行 (IntradayBidRow) に
// 変換する純関数。 SpotBidding の `models/convertSpotBidsFromApprovalRequest.ts` と同じ
// 「proto → design 忠実 表示行」 パターン。 design 忠実 UI (KomaRibbon / PositionChart /
// IntradayBidTable / aggregation) は IntradayBidRow を消費するため、 RPC 実データをこの型に寄せる。
//
// 数量は保持・表示とも コマ別 MW (issue #4285) なので単位換算は無い:
// IntradayBidRow.volumeMw / execVolumeMw は型ドキュメント通り MW を保持し、
// 下流 (一覧 / ポジション集計 / 充足率 / KPI) も換算なしで消費する。
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'

import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { bidIssuerTypeOf } from '~/models/bidIssuerType'
import { formatJstTimeHm } from '~/models/dateTime'
import {
  type IntradayBidRow,
  isWithdrawnOrder,
} from '~/pages/IntradayBidding/models/intradayBidRow'

/**
 * proto Timestamp(gc_at) → "HH:MM" 表示 (= コマ別 GC 締切)。
 */
const gcDeadlineLabel = (order: IntradayOrderRow): string => {
  if (order.gcAt === undefined) return '—'
  return formatJstTimeHm(timestampDate(order.gcAt))
}

const decimalToString = (d: IntradayOrderRow['priceYenPerKwh']): string =>
  d?.value ?? '0'

/** Decimal → string。 未設定 (= 未約定等で値が無い) は undefined のまま返す。 */
const decimalToOptionalString = (d: Decimal | undefined): string | undefined =>
  d?.value

/**
 * 数量 Decimal → コマ別 MW 数値。 未設定は undefined (= 呼び出し側が「—」等に倒す)。
 *
 * read seam の外 (板情報タブの自社入札表など、 RPC 実データを IntradayBidRow に
 * 通さず直接描画する箇所) からも同じ入口を使えるよう export する。
 */
export const decimalVolumeToMw = (
  d: IntradayOrderRow['volumeMwh'],
): number | undefined => (d === undefined ? undefined : Number(d.value))

/** 数量 Decimal string → MW string (= IntradayBidRow の保持単位)。 */
const decimalVolumeToMwString = (d: IntradayOrderRow['volumeMwh']): string =>
  String(decimalVolumeToMw(d) ?? 0)

export const convertIntradayBidFromOrder = (
  order: IntradayOrderRow,
): IntradayBidRow => ({
  id: order.id,
  areaCode: order.areaCode,
  timeCode: order.timeCode,
  direction: order.direction,
  priceYenPerKwh: decimalToString(order.priceYenPerKwh),
  volumeMw: decimalVolumeToMwString(order.volumeMwh),
  bidStatus: order.bidStatus,
  // 一覧 RPC (`BiddingIntradayOrderListItem`) の approval_state をそのまま持つ。
  // 送信軸 / 業務ステータスからの推定はしない。 ステータス列 (AC-BIN-035) の
  // 「承認待ち」「差戻し」は composeIntradayTableStatus がこの実値から導出する
  // (独立した承認列が無いので、 表示用の写像は持たない)。
  approvalState: order.approvalState,
  withdrawn: isWithdrawnOrder(order),
  sendStatus: order.sendStatus,
  // AC-BIN-059: 送信失敗理由カテゴリ / 生 detail もそのまま流す
  // (`SendErrorReasonTooltip` が hover 表示に使う)。
  sendErrorCategory: order.sendErrorCategory,
  sendErrorDetail: order.sendErrorDetail,
  withdrawState: order.withdrawState,
  withdrawFailureReason: order.withdrawFailureReason,
  withdrawable: order.withdrawable,
  returnableToDraft: order.returnableToDraft,
  fromCompletedWorkflow: order.fromCompletedWorkflow,
  receiptChecking: order.receiptChecking,
  approvalActionable: order.approvalActionable,
  updatedAt: order.updatedAt,
  gcAt: order.gcAt,
  requestedBy: order.requestedBy,
  currentStageApproverMemberIds: order.currentStageApproverMemberIds,
  updatedBy: order.updatedBy,
  createdAt: order.createdAt,
  createdBy: order.createdBy,
  workflowId: order.workflowId,
  gcDeadline: gcDeadlineLabel(order),
  execVolumeMw:
    order.matchedVolumeMwh !== undefined
      ? decimalVolumeToMwString(order.matchedVolumeMwh)
      : undefined,
  matchedPriceYenPerKwh: decimalToOptionalString(order.matchedPriceYenPerKwh),
  lastMatchedAt: order.lastMatchedAt,
  remainingVolumeMw:
    order.remainingVolumeMw !== undefined
      ? decimalVolumeToMwString(order.remainingVolumeMw)
      : undefined,
  withdrawAt: order.withdrawAt,
  deliveryContractCd: order.deliveryContractCd,
  memoFree: order.memoFree,
  memoJepx: order.memoJepx,
  jepxBidNo: order.jepxBidNo,
  needsReview: order.needsReview,
  needsReviewSince: order.needsReviewSince,
  // 発注主体は proto から取れるようになった (issue #5366)。
  issuerType: bidIssuerTypeOf(order.issuerType),
  orderMethod: order.orderMethod,
})

export const convertIntradayBidsFromOrders = (
  orders: readonly IntradayOrderRow[],
): IntradayBidRow[] => orders.map(convertIntradayBidFromOrder)
